import { type WorkflowDetails, type WorkflowStepInput } from "~/api/types";
import { type PatchWorkflowData } from "~/api/v2/requests";
import { toISODateString, toISODateTimeString } from "~/helpers/date";
import { z } from "zod";
import i18n from "~/i18n";

const todayAtMidnight = new Date().setHours(0, 0, 0, 0);

export const workflowEditorFormSchema = z
  .object({
    reviewFrom: z.date().nullable(),
    reviewTo: z.date().nullable(),
    // We use these for validation of review dates
    actualStartDate: z.date().nullable(),
    actualEndDate: z.date().nullable(),
    description: z.string(),
    steps: z.array(z.custom<WorkflowStepInput>()).refine(
      (steps) => steps.every((step) => step.name.trim().length > 0),
      i18n.t(($) => $.validations.allStepsMustHaveAName),
    ),
  })
  .refine(
    (data) =>
      data.reviewFrom && data.actualStartDate === null
        ? new Date(data.reviewFrom).setHours(0, 0, 0, 0) >= todayAtMidnight
        : true,
    {
      message: i18n.t(($) => $.validations.startDateMustNotBeInPast),
      path: ["reviewFrom"],
    },
  )
  .refine(
    (data) =>
      data.reviewTo && data.actualEndDate === null
        ? new Date(data.reviewTo).setHours(0, 0, 0, 0) >= todayAtMidnight
        : true,
    {
      message: i18n.t(($) => $.validations.endDateMustNotBeInPast),
      path: ["reviewTo"],
    },
  )
  .refine(
    (data) =>
      data.reviewFrom && data.reviewTo && data.actualStartDate === null
        ? data.reviewTo > data.reviewFrom
        : true,
    {
      message: i18n.t(($) => $.validations.endDateAfter),
      path: ["reviewTo"],
    },
  );

export type WorkflowEditorFormValues = z.infer<typeof workflowEditorFormSchema>;

export function mapWorkflowToFormValues(
  workflow: WorkflowDetails | undefined,
): WorkflowEditorFormValues | undefined {
  if (!workflow) return undefined;

  return {
    reviewFrom: workflow.documentVersion.reviewFrom
      ? new Date(workflow.documentVersion.reviewFrom)
      : null,
    reviewTo: workflow.documentVersion.reviewTo
      ? new Date(workflow.documentVersion.reviewTo)
      : null,
    actualStartDate: workflow.stepInstances[0]?.createdAt
      ? new Date(workflow.stepInstances[0].createdAt)
      : null,
    actualEndDate: workflow.documentVersion.publishedAt
      ? new Date(workflow.documentVersion.publishedAt)
      : null,
    description: workflow.description ?? "",
    steps: workflow.steps.map((step) => ({ ...step, _key: `${step.id}` })),
  };
}

export function mapFormValuesToPatchWorkflowData(
  values: WorkflowEditorFormValues,
  dirtyFields: Partial<Record<keyof WorkflowEditorFormValues, unknown>>,
): PatchWorkflowData {
  const data: PatchWorkflowData = {};

  if (dirtyFields.reviewFrom) {
    data.reviewFrom = toISODateString(values.reviewFrom);
  }
  if (dirtyFields.reviewTo) {
    data.reviewTo = toISODateString(values.reviewTo);
  }
  if (dirtyFields.description) {
    data.description = values.description;
  }
  if (dirtyFields.steps) {
    data.steps = values.steps.map((step) => ({
      id: step.id,
      name: step.name.trim(),
      description: step.description ?? null,
      type: step.type,
      dueAt: step.dueAt ? toISODateTimeString(step.dueAt) : null,
      users: step.users.map((user) => ({
        userId: user.userId,
        permission: user.permission,
      })),
    }));
  }

  return data;
}
